<script setup lang="ts">
import GaiaText from '../base/GaiaText.vue';
import { gaiaNeutral } from '../themes/tokens';

const props = defineProps({
  /** Uppercase cell label, e.g. "Contract", "Kind", "Created". */
  label: {
    type: String,
    required: true,
  },
  /**
   * Label above the value, for a grid GaiaMetaStrip. Adds the `#icon` (before
   * the value), `#actions` (top right, by the label) and `#sub` (a line under
   * the value) slots.
   */
  stacked: {
    type: Boolean,
    default: false,
  },
  /** The cell opens an editor: pointer, hover surface, and `#actions` shown on hover only. */
  clickable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{ click: [] }>();

const hoverBg = gaiaNeutral.surface;
</script>

<template>
  <!-- One cell of GaiaMetaStrip: label + value (text, link, tag, badge). The
       root stays a plain flex div — GaiaMetaStrip draws the cell dividers via
       :deep(.gaia-meta-item + .gaia-meta-item), so the class must survive. -->
  <n-flex
    v-if="props.stacked"
    vertical
    :size="3"
    class="gaia-meta-item stacked"
    :class="{ clickable: props.clickable }"
    :style="{ padding: '9px 16px', minWidth: 0 }"
    @click="props.clickable && emit('click')"
  >
    <!-- Fixed row heights (label row as tall as an action button, value row as
         tall as a state icon) so labels and values line up across the strip
         whether or not a cell has actions or an icon. -->
    <n-flex
      align="center"
      justify="space-between"
      :size="8"
      :wrap="false"
      :style="{ minHeight: '20px' }"
    >
      <gaia-text variant="label" size="micro" tone="faint">{{ props.label }}</gaia-text>
      <n-flex v-if="$slots.actions" align="center" :size="4" :wrap="false" class="actions">
        <slot name="actions" />
      </n-flex>
    </n-flex>
    <n-flex align="center" :size="8" :wrap="false" :style="{ minWidth: 0, minHeight: '24px' }">
      <slot name="icon" />
      <!-- A div: the value can be a select or a state cell, not just text. -->
      <gaia-text
        tag="div"
        size="dense"
        tone="ink"
        weight="semibold"
        :style="{ flex: '1 1 auto', minWidth: 0, fontVariantNumeric: 'tabular-nums' }"
      >
        <slot />
      </gaia-text>
    </n-flex>
    <gaia-text v-if="$slots.sub" size="meta" tone="tertiary">
      <slot name="sub" />
    </gaia-text>
  </n-flex>
  <n-flex
    v-else
    align="center"
    :size="8"
    :wrap="false"
    class="gaia-meta-item"
    :style="{ padding: '11px 14px', minWidth: 0 }"
  >
    <gaia-text variant="label" size="micro" tone="faint" :style="{ flex: 'none' }">
      {{ props.label }}
    </gaia-text>
    <gaia-text
      size="dense"
      tone="text"
      :style="{ minWidth: 0, fontVariantNumeric: 'tabular-nums' }"
    >
      <slot />
    </gaia-text>
  </n-flex>
</template>

<style scoped>
.stacked.clickable {
  cursor: pointer;
}

.stacked.clickable:hover {
  background: v-bind(hoverBg);
}

/* An editable cell's pencil only shows under the pointer or on focus. */
.stacked.clickable .actions {
  opacity: 0;
}

.stacked.clickable:hover .actions,
.stacked.clickable .actions:focus-within {
  opacity: 1;
}

/* The first and last cells round with the strip, so the hover surface does too. */
.stacked:first-child {
  border-radius: 10px 0 0 10px;
}

.stacked:last-child {
  border-radius: 0 10px 10px 0;
}
</style>
